// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { Bundle, ClaimRow, Dim, Val } from './types';

const FILES = ['meta', 'fatalities', 'claims', 'enforcement', 'hazards'] as const;

export async function loadBundle(signal?: AbortSignal): Promise<Bundle> {
  const parts = await Promise.all(
    FILES.map(async (name) => {
      const res = await fetch(`data/${name}.json`, { signal });
      if (!res.ok) throw new Error(`Could not load ${name}.json (HTTP ${res.status})`);
      return res.json();
    }),
  );
  const [meta, fatalities, claims, enforcement, hazards] = parts;
  return { meta, fatalities, claims, enforcement, hazards } as Bundle;
}

// ── Selectors ──────────────────────────────────────────────────────────────
//
// Every one of these excludes parent rows, `nfd` rows and the published Total
// from rankings. Leaving any of them in silently ranks a category against its
// own parent.

export const isLeaf = (r: { level: number; total: boolean; nfd: boolean }) => r.level === 2 && !r.total && !r.nfd;
export const isParent = (r: { level: number; total: boolean }) => r.level === 1 && !r.total;

export function divisions(rows: ClaimRow[]): ClaimRow[] {
  return rows.filter((r) => isParent(r) && /^[A-Z]$/.test(r.code));
}

export function subdivisions(rows: ClaimRow[]): ClaimRow[] {
  return rows.filter(isLeaf);
}

export function totalOf(rows: ClaimRow[]): ClaimRow | undefined {
  return rows.find((r) => r.total);
}

export function findByCode(rows: ClaimRow[], code: string): ClaimRow | undefined {
  return rows.find((r) => r.code === code);
}

export function dimTotal(rows: Dim[]): Dim | undefined {
  return rows.find((r) => r.total);
}

/**
 * Attach each subdivision to its ANZSIC division. Order in the source IS the
 * hierarchy — there is no parent key — so this walks the array rather than
 * inferring the parent from the code (subdivision "01" does not name "A").
 */
export function withDivision(rows: ClaimRow[]): (ClaimRow & { division: string; divisionName: string })[] {
  const out: (ClaimRow & { division: string; divisionName: string })[] = [];
  let division = '';
  let divisionName = '';
  for (const r of rows) {
    if (r.total) continue;
    if (r.level === 1) { division = r.code; divisionName = r.name; continue; }
    if (r.nfd) continue;
    out.push({ ...r, division, divisionName });
  }
  return out;
}

/** Same walk for the fatality tables, which use the Dim shape. */
export function withDivisionDim(rows: Dim[]): (Dim & { division: string; divisionName: string })[] {
  const out: (Dim & { division: string; divisionName: string })[] = [];
  let division = '';
  let divisionName = '';
  for (const r of rows) {
    if (r.total) continue;
    if (r.level === 1) { division = r.code; divisionName = r.name; continue; }
    if (r.nfd) continue;
    out.push({ ...r, division, divisionName });
  }
  return out;
}

/** The most recent period at which a given measure is actually published. */
export function latestPublishedIndex(rows: { [k: string]: unknown }[], key: string, periods: string[]): number {
  for (let i = periods.length - 1; i >= 0; i--) {
    const any = rows.some((r) => {
      const series = r[key] as Val[] | undefined;
      return Array.isArray(series) && series[i] !== null && series[i] !== undefined;
    });
    if (any) return i;
  }
  return periods.length - 1;
}

export function sum(values: Val[]): number {
  return values.reduce<number>((a, b) => a + (b ?? 0), 0);
}
